import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiShield, FiAlertTriangle, FiFileText, FiLock,
  FiUser, FiGlobe, FiChevronDown, FiInfo, FiEye, FiServer
} = FiIcons;

const sections = [
  {
    id: 'S1',
    icon: FiInfo,
    title: 'Autonomous Intelligence Limitations',
    badge: 'Section I',
    color: 'text-sonar-green',
    borderColor: 'border-sonar-green',
    bgColor: 'bg-sonar-green/10',
    summary: 'OMEN v4 is an autonomous OSINT engine. It does not exercise human judgment or conduct proprietary field investigations.',
    body: `CreditSonar operates as an autonomous Open-Source Intelligence (OSINT) engine designed to aggregate, process, and synthesize publicly available data. As an automated system, OMEN v4 does not exercise human judgment, conduct proprietary field investigations, or independently authenticate the completeness of third-party data sources. All reports are provided strictly on an "as-is" basis.`,
  },
  {
    id: 'S6',
    icon: FiServer,
    title: 'Fair Usage & System Abuse Prohibitions',
    badge: 'Section VI',
    color: 'text-pink-400',
    borderColor: 'border-pink-500',
    bgColor: 'bg-pink-500/10',
    summary: 'Strict prohibitions against automated bot abuse, credential stuffing, and unauthorized API exploitation to protect system integrity.',
    body: `To ensure platform stability and protect against resource exhaustion, CreditSonar strictly enforces fair usage limits and prohibits any form of system abuse.

ACCOUNT SECURITY: Users are solely responsible for securing their credentials and API keys. Keys must never be hard-coded in front-end code, stored in public repositories, or shared across unauthorized integrations. Any usage originating from a user's account or API key—including usage resulting from Account Takeover (ATO) — remains the user's responsibility.

PROHIBITED ACTIVITIES:
1. Credential stuffing, brute-force login attempts, or the creation of fake accounts to abuse free tiers or evade credit caps.
2. Deployment of bots or automated scraping tools against CreditSonar infrastructure.
3. Attempts to reverse-engineer, stress-test, or intentionally exhaust system resources, tokens, or API quotas.

SYSTEM ENFORCEMENT: CreditSonar employs advanced security layers including Web Application Firewalls (WAF), rate limiting, and strict quota enforcement. All actions, including credit-consuming API calls, are continuously audited and logged. We reserve the right to challenge suspicious traffic, revoke keys, and permanently ban entities identified as engaging in abusive behavior.`,
  },
  {
    id: 'S7',
    icon: FiAlertTriangle,
    title: 'Limitation of Liability',
    badge: 'Section VII',
    color: 'text-red-400',
    borderColor: 'border-red-500',
    bgColor: 'bg-red-500/10',
    summary: 'The service provider shall not be liable for any damages resulting from reliance on OMEN v4 outputs.',
    body: `TO THE FULLEST EXTENT PERMITTED BY LAW, THE SERVICE PROVIDER SHALL NOT BE LIABLE FOR ANY DAMAGES ARISING OUT OF THE USE OR INABILITY TO USE THE PLATFORM. ALL DECISIONS MADE BASED ON OMEN v4 OUTPUTS ARE THE SOLE RESPONSIBILITY OF THE USER.`,
  },
];

const SectionCard = ({ section, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.07 }}
      className={`bg-sonar-card border rounded-2xl overflow-hidden transition-all duration-300 ${
        open ? section.borderColor : 'border-sonar-border hover:border-sonar-green/30'
      }`}
    >
      <button onClick={() => setOpen(o => !o)} className="w-full text-left p-6 flex items-start gap-5 group">
        <div className={`w-11 h-11 rounded-xl ${section.bgColor} flex items-center justify-center shrink-0 border ${section.borderColor} mt-0.5`}>
          <SafeIcon icon={section.icon} className={`text-lg ${section.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <span className={`text-[9px] font-black uppercase tracking-[0.25em] font-mono ${section.color}`}>{section.badge}</span>
          </div>
          <h3 className="text-sm font-black text-white uppercase tracking-wide leading-tight mb-2">{section.title}</h3>
          <p className="text-xs text-slate-400 font-mono leading-relaxed pr-6">{section.summary}</p>
        </div>
        <SafeIcon icon={FiChevronDown} className={`text-slate-500 shrink-0 mt-1 transition-transform duration-300 ${open ? 'rotate-180 ' + section.color : 'group-hover:text-sonar-green'}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-6 pb-6 border-t border-sonar-border pt-5">
              <p className="text-xs text-slate-400 font-mono whitespace-pre-wrap leading-relaxed">{section.body}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const LegalNoticePage = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-16">
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-400 px-4 py-1.5 rounded-full text-[10px] font-black border border-amber-500/30 tracking-[0.25em] uppercase mb-6">
          <SafeIcon icon={FiFileText} /> Operational Terms
        </div>
        <h1 className="text-4xl font-black text-white mb-6 uppercase">Terms of <span className="text-amber-400">Use.</span></h1>
        <p className="text-sm text-slate-400 max-w-2xl mx-auto font-mono"> Governing rules for the OMEN v4 intelligence stack. Strict anti-abuse protocols enforced. </p>
      </div>
      <div className="space-y-3">
        {sections.map((section, i) => <SectionCard key={section.id} section={section} index={i} />)}
      </div>
    </div>
  );
};

export default LegalNoticePage;